import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Button, Chip, Tooltip } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import React from "react";
import { useDispatch } from "react-redux";

import { updateBusStopStatus } from "../store/transport-slice";
import { BusStop } from "../store/transport-types";

interface GridColumn {
	field: string;
	type: string;
	minWidth?: number;
	width?: number;
	flex?: number;
	align?: "left" | "center" | "right";
	headerName?: string;
	renderHeader?: () => React.ReactNode;
	renderCell?: (params: { row: any }) => React.ReactNode;
	valueGetter?: (params: { row: any }) => any;
	sortable?: boolean;
	cellClassName?: string;
	headerClassName?: string;
}

interface UseBusStopColumnsProps {
	locationId: number;
	handleOpenEdit: (row: BusStop) => void;
}

const useBusStopColumns = ({
	locationId,
	handleOpenEdit,
}: UseBusStopColumnsProps) => {
	const confirm = useConfirm();
	const dispatch = useDispatch();

	// Stops are never deleted. Deactivating hides the stop from workers while
	// leaving it readable on the rides already booked against it.
	const handleDeactivate = (id: number, name: string) => {
		confirm({
			title: "Deactivate stop",
			content: `Workers will no longer be able to choose "${name}". Rides already booked from it still go ahead, and past orders keep showing it. Anyone who has it saved as their usual stop will be asked to pick a new one.`,
			cancellationText: "Cancel",
			confirmationText: (
				<Button variant='contained' color='error'>
					Deactivate
				</Button>
			),
		})
			.then(() =>
				dispatch(updateBusStopStatus({ id, isActive: false, locationId }))
			)
			.catch(() => undefined);
	};

	const handleActivate = (id: number, name: string) => {
		confirm({
			title: "Activate stop",
			content: `"${name}" will be offered to workers booking transport at this location again.`,
			cancellationText: "Cancel",
			confirmationText: (
				<Button variant='contained' color='primary'>
					Activate
				</Button>
			),
		})
			.then(() =>
				dispatch(updateBusStopStatus({ id, isActive: true, locationId }))
			)
			.catch(() => undefined);
	};

	const columns: GridColumn[] = [
		{
			field: "name",
			type: "string",
			minWidth: 240,
			flex: 1,
			headerName: "Stop",
			renderHeader: () => (
				<span style={{ padding: "0px 4px", fontWeight: "bold" }}>Stop</span>
			),
			renderCell: (params) => (
				<Tooltip title={params.row.name}>
					<Box
						sx={{
							padding: "0px 4px",
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "14px",
							color: "rgba(0,0,0,0.87)",
							textOverflow: "ellipsis",
							overflow: "hidden",
						}}
					>
						{params.row.name}
					</Box>
				</Tooltip>
			),
			valueGetter: (params) => params.row.name,
		},
		{
			field: "isActive",
			type: "boolean",
			minWidth: 140,
			flex: 1,
			headerName: "Status",
			renderHeader: () => (
				<span style={{ padding: "0px 4px", fontWeight: "bold" }}>Status</span>
			),
			renderCell: (params) => (
				<Chip
					label={params.row.isActive ? "Active" : "Inactive"}
					size='small'
					color={params.row.isActive ? "success" : "default"}
					variant={params.row.isActive ? "filled" : "outlined"}
				/>
			),
			valueGetter: (params) => params.row.isActive,
		},
		{
			field: "actions",
			type: "actions",
			flex: 1,
			sortable: false,
			align: "left",
			cellClassName: "hover-column-cell",
			headerClassName: "hover-column-header",
			renderHeader: () => <></>,
			renderCell: (params: any) => (
				<Box
					className='hover-content'
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-start",
						gap: "8px",
						padding: "8px 12px",
					}}
				>
					{params.row.isActive ? (
						<Box
							sx={{
								color: "#6c6c6c",
								"&:hover": {
									color: "#000000",
									transform: "scale(1.2)",
								},
								cursor: "pointer",
							}}
						>
							<Tooltip title='Rename'>
								<EditIcon
									id={`edit-bus-stop-${params.row.id}`}
									fontSize='small'
									onClick={() => handleOpenEdit(params.row)}
								/>
							</Tooltip>
						</Box>
					) : null}
					{params.row.isActive ? (
						<Box
							sx={{
								color: "#6c6c6c",
								"&:hover": { color: "red", transform: "scale(1.2)" },
								cursor: "pointer",
							}}
						>
							<Tooltip title='Deactivate'>
								<VisibilityOffIcon
									id={`toggle-bus-stop-status-${params.row.id}`}
									fontSize='small'
									onClick={() =>
										handleDeactivate(params.row.id, params.row.name)
									}
								/>
							</Tooltip>
						</Box>
					) : (
						<Box
							sx={{
								color: "#6c6c6c",
								"&:hover": {
									color: "#000000",
									transform: "scale(1.2)",
								},
								cursor: "pointer",
							}}
						>
							<Tooltip title='Activate'>
								<VisibilityIcon
									id={`toggle-bus-stop-status-${params.row.id}`}
									fontSize='small'
									onClick={() =>
										handleActivate(params.row.id, params.row.name)
									}
								/>
							</Tooltip>
						</Box>
					)}
				</Box>
			),
		},
	];

	return { columns };
};

export default useBusStopColumns;
